// lib/telefonie/anrufWaechter.ts
// Entscheidet, welcher Anruf auf der Karte steht. Getrennt vom Realtime-Abo,
// damit genau diese Regeln prüfbar sind.
import { zuAnrufKarte, type AnrufKarte, type AnrufZeile } from './anrufKarte';

/** Wie lange ein beendeter oder verpasster Anruf noch stehen bleibt. Lang genug,
 *  um Name und Dauer zu lesen, kurz genug, um nicht im Weg zu sein. */
export const AUSBLENDEN_NACH_MS = 8000;

/**
 * Nächster Kartenzustand aus einer eingetroffenen Anruf-Zeile.
 * `null` heißt: nichts ändern bzw. nichts zeigen.
 *
 * Die eine nicht offensichtliche Regel: Ein bereits erledigter Anruf, der NICHT
 * der gerade gezeigte ist, wird ignoriert. Sonst könnte ein nachlaufendes
 * Ereignis eines älteren Anrufs die Karte eines gerade klingelnden verdrängen —
 * und Webex stellt nachweislich nicht in Reihenfolge zu.
 *
 * Verpasste Anrufe erscheinen seit 17.08.2026 GAR NICHT mehr (Ansage des Users:
 * „nicht in der Pille als Meldung, im Widget reicht es"). Sie zählen weiter im
 * Verpasst-Zähler der Telefon-Kachel und stehen in der Anrufliste.
 */
export function naechsteKarte(gezeigteId: string | null, zeile: AnrufZeile | null): AnrufKarte | null {
  if (!zeile) return null;
  const karte = zuAnrufKarte(zeile);
  if (karte.verpasst) return null;
  if (karte.erledigt && gezeigteId !== zeile.id) return null;
  return karte;
}

/**
 * Ein klingelnder Anruf, der ins „verpasst" kippt, steht bereits als Karte —
 * `naechsteKarte` liefert dafür nur noch `null` („nichts ändern"), die Karte
 * bliebe also stehen. Deshalb dieses zweite Signal: sie geht sofort zu, statt
 * acht Sekunden lang „Verpasst" zu melden.
 */
export function verpasstSchliesstKarte(gezeigteId: string | null, zeile: AnrufZeile | null): boolean {
  if (!zeile || !gezeigteId) return false;
  return zeile.status === 'verpasst' && zeile.id === gezeigteId;
}

/**
 * Verwerfung: Wurde eine Karte manuell weggeklickt (statt vom eigenen
 * Ausblend-Timer geräumt), gilt ihre Anruf-ID als verworfen — nachlaufende
 * Realtime-Ereignisse dazu (Statuswechsel beim Abnehmen/Auflegen) dürfen die
 * Karte nicht erneut aufreißen. Drei reine Funktionen, damit der Hook nur noch
 * ausführt, was hier entschieden wurde.
 */

/** Soll das Ereignis zur `eingehendeId` übersprungen werden, weil sie gerade
 *  als verworfen gilt? */
export function istVerworfen(verworfenId: string | null, eingehendeId: string): boolean {
  return verworfenId !== null && verworfenId === eingehendeId;
}

/** Neuer Verwerfungs-Stand nach einem eingetroffenen Ereignis: Eine ANDERE ID
 *  als die verworfene hebt die Verwerfung auf, damit ein neuer Anruf nicht an
 *  einer alten Verwerfung hängen bleibt. */
export function naechsteVerwerfung(verworfenId: string | null, eingehendeId: string): string | null {
  if (verworfenId !== null && verworfenId !== eingehendeId) return null;
  return verworfenId;
}

/** Verwerfungs-Stand, wenn die Karte für `geschlosseneId` schließt.
 *
 *  `vomTimerGeraeumteId` ist die Anruf-ID, die der eigene Ausblend-Timer
 *  zuletzt geräumt hat (oder `null`). Bewusst ein ID-Vergleich statt eines
 *  bloßen Wahrheitswerts: Ein Timer, der VOR dem Ablauf durch einen manuellen
 *  Klick überholt wird, feuert trotzdem noch acht Sekunden später — ein
 *  reines Flag bliebe dann fälschlich "true" stehen und würde den nächsten,
 *  eigentlich zu verwerfenden Anruf fälschlich für "vom Timer geräumt" halten.
 *  Der Identitätsvergleich ist dagegen von Natur aus robust: Ein verwaister
 *  Timer-Aufruf für eine ALTE ID kann nie mit der ID eines späteren, anderen
 *  Anrufs übereinstimmen. */
export function verwerfenBeimSchliessen(
  geschlosseneId: string | null,
  vomTimerGeraeumteId: string | null,
): string | null {
  if (!geschlosseneId || geschlosseneId === vomTimerGeraeumteId) return null;
  return geschlosseneId;
}
